import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { FAQAccordion } from "../components/FAQAccordion";
import { ServiceIcon, IndustryIcon, IconCheck, IconArrow } from "../components/Icons";
import { getService, getServices, getIndustries, mediaUrl } from "../lib/api";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setError(false);
    getService(slug).then(setService).catch(() => setError(true)).finally(() => setLoading(false));
    getServices().then(setAllServices).catch(() => {});
    getIndustries().then(setIndustries).catch(() => {});
  }, [slug]);

  if (error) return <NotFound />;
  if (loading || !service) return <div style={{ minHeight: "60vh" }} />;

  const indMap = Object.fromEntries(industries.map((i) => [i.slug, i]));
  const related = allServices.filter((s) => (service.related || []).includes(s.slug));

  return (
    <div data-testid="service-detail-page">
      <PageHeader eyebrow={service.hero_eyebrow} title={service.title} lead={service.hero_lead} crumb={service.title}
        image={mediaUrl(service.image_media_id)} imageAlt={service.image_alt} />

      {/* OVERVIEW */}
      <section className="detail-section bg-paper" data-testid="section-overview">
        <div className="wrap">
          <Reveal className="d-lead">
            <div className="ic" style={{ width: 56, height: 56, borderRadius: 14, background: "var(--mist-2)", display: "grid", placeItems: "center", marginBottom: 22 }}>
              <ServiceIcon name={service.icon} />
            </div>
            <span className="eyebrow">Overview</span>
            <h2>{service.summary}</h2>
            {service.overview?.map((p, i) => <p key={i}>{p}</p>)}
          </Reveal>
          {(service.secondary_media_id || service.secondary2_media_id) && (
            <div className="photo-strip" style={{ marginTop: 36 }} data-testid="service-photos">
              {[[service.secondary_media_id, service.secondary_alt], [service.secondary2_media_id, service.secondary2_alt]]
                .filter(([id]) => id)
                .map(([id, alt], i) => (
                  <Reveal key={id} delay={i * 60}>
                    <figure className="photo-card"><img src={mediaUrl(id)} alt={alt || ""} loading="lazy" /></figure>
                  </Reveal>
                ))}
            </div>
          )}
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="detail-section bg-mist" data-testid="section-challenges">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">Challenges we address</span><h2>The problems this service solves</h2></Reveal>
          <div className="info-grid-2" style={{ marginTop: 30 }}>
            {service.challenges?.map((ch, i) => (
              <Reveal key={i} className="mini-card" delay={(i % 2) * 60} data-testid="service-challenge">
                <span className="mc-k">{String(i + 1).padStart(2, "0")}</span>
                <h3>{ch.title}</h3>
                <p>{ch.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SCOPE */}
      <section className="detail-section bg-paper" data-testid="section-scope">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">Scope of support</span><h2>What our engagement covers</h2></Reveal>
          <Reveal className="pill-list" style={{ marginTop: 26 }}>
            {service.scope?.map((s) => <span key={s} data-testid="service-scope">{s}</span>)}
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="detail-section bg-mist" data-testid="section-process">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">Our process</span><h2>How we deliver</h2></Reveal>
          <div className="steps" style={{ marginTop: 30 }}>
            {service.process?.map((p, i) => (
              <Reveal key={i} className="step" delay={(i % 4) * 60} data-testid="service-process-step">
                <div className="sn">{p.number}</div>
                <h3>{p.title}</h3>
                <p>{p.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERABLES + BENEFITS */}
      <section className="detail-section bg-paper" data-testid="section-deliverables">
        <div className="wrap detail-grid">
          <Reveal>
            <span className="eyebrow">Expected deliverables</span>
            <h2 style={{ fontSize: "1.5rem", margin: "14px 0 20px" }}>What you receive</h2>
            <ul className="chk-list">
              {service.deliverables?.map((d) => <li key={d} data-testid="service-deliverable"><IconCheck /> {d}</li>)}
            </ul>
          </Reveal>
          <Reveal delay={80}>
            <span className="eyebrow">Client benefits</span>
            <h2 style={{ fontSize: "1.5rem", margin: "14px 0 20px" }}>The value we create</h2>
            <ul className="chk-list">
              {service.benefits?.map((b) => <li key={b} data-testid="service-benefit"><IconCheck /> {b}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* RELEVANT INDUSTRIES */}
      {service.industries?.length > 0 && (
        <section className="detail-section bg-mist" data-testid="section-industries">
          <div className="wrap">
            <Reveal className="d-lead"><span className="eyebrow">Relevant industries</span><h2>Sectors we support with this service</h2></Reveal>
            <div className="hub-grid three" style={{ marginTop: 30 }}>
              {service.industries.map((iSlug, i) => {
                const ind = indMap[iSlug];
                if (!ind) return null;
                return (
                  <Reveal key={iSlug} delay={(i % 3) * 60}>
                    <Link to={`/industries/${ind.slug}`} className="hub-card" data-testid={`service-industry-${ind.slug}`}>
                      <div className="hc-ic"><IndustryIcon name={ind.icon} /></div>
                      <h3>{ind.title}</h3>
                      <p>{ind.summary}</p>
                      <span className="more">Explore sector <IconArrow /></span>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="detail-section bg-paper" data-testid="section-faqs">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">FAQs</span><h2>Frequently asked questions</h2></Reveal>
          <div style={{ marginTop: 30 }}><FAQAccordion items={service.faqs} testid="service-faq" /></div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {related.length > 0 && (
        <section className="detail-section bg-mist" data-testid="section-related">
          <div className="wrap">
            <Reveal className="d-lead"><span className="eyebrow">Related services</span><h2>Explore what pairs well with this</h2></Reveal>
            <div className="hub-grid" style={{ marginTop: 30 }}>
              {related.map((r, i) => (
                <Reveal key={r.id} delay={(i % 2) * 60}>
                  <Link to={`/services/${r.slug}`} className="hub-card" data-testid={`related-${r.slug}`}>
                    <span className="hc-num">{r.number}</span>
                    <div className="hc-ic"><ServiceIcon name={r.icon} /></div>
                    <h3>{r.title}</h3>
                    <p>{r.summary}</p>
                    <span className="more">Learn more <IconArrow /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/services" className="btn btn-ghost" data-testid="all-services-link">All services</Link>
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
